import React, { useState, useEffect, useMemo } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import * as d3 from 'd3-geo';
import { feature } from 'topojson-client';

// High-quality TopoJSON for the world map
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

interface Location {
  name: string;
  coordinates: [number, number]; // [lon, lat]
  region: string;
}

const LocationsSection: React.FC = () => {
  const [geographies, setGeographies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const officeLocations: Location[] = [
    { name: "Dubai", coordinates: [55.2708, 25.2048], region: "Middle East Hub" },
    { name: "Kinshasa", coordinates: [15.2663, -4.4419], region: "Central African Operations" },
    { name: "Harare", coordinates: [31.0335, -17.8252], region: "Southern African Advisory" },
    { name: "Johannesburg", coordinates: [28.0473, -26.2041], region: "Strategic Capital Desk" }
  ];

  // Map projection settings
  const width = 1000;
  const height = 500;
  
  const projection = useMemo(() => {
    return d3.geoMercator()
      .scale(350) 
      .center([25, 7])
      .translate([width / 2, height / 2]);
  }, []);

  const pathGenerator = useMemo(() => {
    return d3.geoPath().projection(projection);
  }, [projection]);

  useEffect(() => {
    fetch(geoUrl)
      .then(res => res.json())
      .then(data => {
        const countries = (feature(data, data.objects.countries) as any).features;
        setGeographies(countries);
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Map data failed to load:", err);
        setIsLoading(false);
      });
  }, []);

  return (
    <section id="locations" className="py-32 bg-[#010712] flex justify-center px-4 scroll-mt-32 overflow-hidden">
      <div className="w-full max-w-[1440px] flex flex-col items-center">
        {/* Header Text - Level 1 Label */}
        <div className="text-center max-w-3xl mb-16">
          <span className="text-[#C5A059] text-[10px] md:text-[11px] font-bold uppercase tracking-[0.3em] mb-6 block">
            Global Network
          </span>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight text-white mb-8">
            Strategic Institutional Presence.
          </h2>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
            Agalliao Corporation maintains key operational hubs in the world's most critical mining corridors, 
            bridging the gap between African assets and global capital.
          </p>
        </div>

        {/* Map Container - Navy styling */}
        <div className="relative w-full aspect-[2/1] bg-[#0a1220]/20 backdrop-blur-sm rounded-none select-none overflow-hidden flex items-center justify-center min-h-[500px] shadow-none">
          {isLoading ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
              <span className="text-[11px] font-black uppercase tracking-widest text-zinc-400">
                Projecting Global Coordinates...
              </span>
            </div>
          ) : (
            <svg 
              viewBox={`0 0 ${width} ${height}`} 
              className="w-full h-full"
              preserveAspectRatio="xMidYMid slice"
            >
              <g className="landmasses">
                {geographies.map((geo, i) => (
                  <path
                    key={`path-${i}`}
                    d={pathGenerator(geo) || ""}
                    fill="#111d30"
                    stroke="#020712"
                    strokeWidth={0.5}
                    className="hover:fill-[#1b2b42] transition-colors duration-300 outline-none"
                  />
                ))}
              </g>

              <path
                d={pathGenerator(d3.geoGraticule().step([10, 10])()) || ""}
                fill="none"
                stroke="#ffffff"
                strokeWidth={0.2}
                strokeOpacity={0.03}
              />

              <g className="markers">
                {officeLocations.map((loc, i) => {
                  const coords = projection(loc.coordinates);
                  if (!coords) return null;
                  const [x, y] = coords;

                  return (
                    <g key={`marker-${i}`} className="group cursor-pointer">
                      <circle cx={x} cy={y} r={30} fill="transparent" className="pointer-events-auto" />
                      
                      <circle
                        cx={x}
                        cy={y}
                        r={15}
                        fill="#C5A059"
                        fillOpacity={0}
                        className="group-hover:fill-opacity-10 transition-all duration-700 group-hover:animate-pulse"
                      />
                      
                      <circle
                        cx={x}
                        cy={y}
                        r={8}
                        fill="#C5A059"
                        fillOpacity={0}
                        className="group-hover:fill-opacity-20 transition-all duration-500 scale-0 group-hover:scale-100 origin-center"
                        style={{ transformOrigin: `${x}px ${y}px` }}
                      />

                      <circle
                        cx={x}
                        cy={y}
                        r={3}
                        fill="#FFFFFF"
                        stroke="#C5A059"
                        strokeWidth={2}
                        className="group-hover:fill-[#C5A059] transition-all duration-300"
                      />

                      {/* Map Tooltips - Glassmorphism */}
                      <foreignObject
                        x={x - 80}
                        y={y - 80}
                        width="160"
                        height="70"
                        className="pointer-events-none overflow-visible"
                      >
                        <div className="flex flex-col items-center opacity-0 group-hover:opacity-100 -translate-y-2 group-hover:translate-y-0 transition-all duration-300 delay-75">
                          <div className="bg-[#020712]/90 backdrop-blur-md px-4 py-2.5 rounded-none flex flex-col items-center text-center">
                            {/* Standardized Title */}
                            <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-[#C5A059] whitespace-nowrap">
                              {loc.name}
                            </span>
                            {/* Standardized Level 1 Metadata */}
                            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mt-1 whitespace-nowrap">
                              {loc.region}
                            </span>
                          </div>
                          <div className="w-[1px] h-3 bg-[#C5A059]/40 mt-2" />
                        </div>
                      </foreignObject>
                    </g>
                  );
                })}
              </g>
            </svg>
          )}
        </div>

        {/* Legend - Standard Level 1 labels with consistent radii */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 opacity-80 hover:opacity-100 transition-all duration-700">
           <div className="flex flex-col items-center text-center p-6 bg-[#0a1220]/40 rounded-none">
             <span className="text-[11px] font-bold uppercase tracking-[0.3em] mb-2 text-white">
               Middle East
             </span>
             <div className="w-6 h-[2px] bg-[#C5A059] mb-3 rounded-full" />
             <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest leading-none">
               Financial Gateway
             </span>
           </div>
           
           <div className="flex flex-col items-center text-center p-6 bg-[#0a1220]/40 rounded-none">
             <span className="text-[11px] font-bold uppercase tracking-[0.3em] mb-2 text-white">
               Sub-Sahara
             </span>
             <div className="w-6 h-[2px] bg-[#C5A059] mb-3 rounded-full" />
             <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest leading-none">
               Asset Management
             </span>
           </div>
           
           <div className="flex flex-col items-center text-center p-6 bg-[#0a1220]/40 rounded-none">
             <span className="text-[11px] font-bold uppercase tracking-[0.3em] mb-2 text-white">
               Central Hub
             </span>
             <div className="w-6 h-[2px] bg-[#C5A059] mb-3 rounded-full" />
             <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest leading-none">
               On-Ground Intelligence
             </span>
           </div>
           
           <div className="flex flex-col items-center text-center p-6 bg-[#0a1220]/40 rounded-none">
             <span className="text-[11px] font-bold uppercase tracking-[0.3em] mb-2 text-white">
               International
             </span>
             <div className="w-6 h-[2px] bg-[#C5A059] mb-3 rounded-full" />
             <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest leading-none">
               Standard Alignment
             </span>
           </div>
        </div>

        {/* Action Button */}
        <div className="mt-20 w-full flex justify-center px-6">
          <a 
            href="https://wa.me/27839287731"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 md:gap-4 bg-[#C5A059] text-zinc-950 px-10 py-5 rounded-none font-bold text-[11px] md:text-[13px] uppercase tracking-[0.1em] md:tracking-[0.2em] hover:bg-[#b08c49] transition-all whitespace-nowrap"
          >
            <MapPin className="w-4 h-4 text-zinc-950 group-hover:animate-bounce" />
            Connect with a Regional Advisor
          </a>
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;
